import { Methods } from "@src/types/api/v1/sessions";
import { apiClient } from "@src/utils/apiClient";
import {
  getErrorCodeFromStatus,
  redirectToErrorPage,
} from "@src/utils/sessionUtils";
import { getSessionStorage, setSessionStorage } from "@src/utils/storage";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

const SESSION_ID_KEY = "session_id";

export const useSession = (sessionInitSetFlg: boolean = true) => {
  const [sessionId, setSessionId] = useState<string | null>(() =>
    getSessionStorage(SESSION_ID_KEY)
  );
  const router = useRouter();

  const setSessionIdToSessionStorage = (newSessionId: string) => {
    setSessionStorage(SESSION_ID_KEY, newSessionId);
    setSessionId(newSessionId);
  };

  // 各フローの最初の画面ではセッションを持たなくても大丈夫だが、それ以外の場合はエラーとしてリダイレクトする
  if (!sessionId && sessionInitSetFlg) {
    // 空のセッションIDをセットする
    setSessionIdToSessionStorage(sessionId);

    // リダイレクト後にダミーの戻り値を返すことで、後続の処理が実行されないようにする
    return {
      sessionId: null,
      createSession: async () => ({ data: null, status: 400 }),
      getSessionInfo: async () => ({ data: null, status: 400 }),
      updateSession: async () => ({ data: null, status: 400 }),
      deleteSession: async () => null,
    };
  }

  // URLパラメータにセッションIDがある場合はそれをセットする
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const sessionId = searchParams.get("session_id");
    const flowType = searchParams.get("flow_type");
    const status = searchParams.get("status");
    if (
      sessionId &&
      status === "success" &&
      (flowType === "マイナンバー登録フロー" ||
        flowType === "本人確認フロー/口座開設情報入力フロー")
    ) {
      setSessionId(sessionId);
    }
  }, []);

  const createSession = async (
    type:
      | "メールアドレス登録フロー"
      | "本人確認フロー/口座開設情報入力フロー"
      | "マイナンバー登録フロー",
    emailAuthKey?: string
  ) => {
    const body = emailAuthKey
      ? { type, email_auth_key: emailAuthKey }
      : { type };

    const response = await apiClient<Methods["post"]["resBody"]>(
      "/v1/sessions",
      {
        method: "POST",
        body,
      }
    );

    // ステータスコードが200以外の場合はエラーページにリダイレクト
    if (response.status !== 200) {
      handleApiError(response.status);
      return null;
    }

    setSessionStorage(SESSION_ID_KEY, response.data.session_id);
    setSessionId(response.data.session_id);
    return {
      data: response.data,
      status: response.status,
    };
  };

  const getSessionInfo = async () => {
    if (!sessionId) throw new Error("セッションが存在しません");

    const response = await apiClient<Methods["get"]["resBody"]>(
      `/v1/sessions?session_id=${sessionId}`,
      {
        method: "GET",
      }
    );
    return {
      data: response.data,
      status: response.status,
    };
  };

  const updateSession = async (
    inputInfo: Omit<Methods["put"]["reqBody"], "session_id">
  ) => {
    if (!sessionId) throw new Error("セッションが存在しません");

    const response = await apiClient<Methods["put"]["resBody"]>(
      "/v1/sessions",
      {
        method: "PUT",
        body: { ...inputInfo, session_id: sessionId },
      }
    );

    return {
      data: response.data,
      status: response.status,
    };
  };

  const deleteSession = async () => {
    if (!sessionId) throw new Error("セッションが存在しません");

    const response = await apiClient<Methods["delete"]["resBody"]>(
      "/v1/sessions",
      {
        method: "DELETE",
        body: { session_id: sessionId },
      }
    );

    // ステータスコードが200以外の場合はエラーページにリダイレクト
    if (response.status !== 200) {
      handleApiError(response.status);
      return null;
    }

    sessionStorage.removeItem(SESSION_ID_KEY);
    setSessionId(null);
    return response.data;
  };

  // APIエラーを処理するヘルパー関数
  const handleApiError = (status: number) => {
    // sessionUtilsの関数を使ってステータスコードからエラーコードを取得
    const errorCode = getErrorCodeFromStatus(status);
    redirectToErrorPage(router, errorCode);
  };

  return {
    sessionId,
    createSession,
    getSessionInfo,
    updateSession,
    deleteSession,
    setSessionIdToSessionStorage,
  };
};
